// ============================================================
// TILT — tiltRelayShell.js        the React/firebase shell around tiltRelay.js
//
// Borrowed from Mini Wili's miniwiliFoursome.js (the borrow-first rule): ONE listener on
// games/tilt/servers/{sid}/relay (run + turn + rows: a few hundred bytes), onDisconnect FIRST, the host
// keeps house, waiting clients pass a stuck turn (staggered; the CAS lets only one win).
// Writes only on a ball start, a ball end, a pass, a run end. Nothing runs per frame.
// Signed out / no server: sid is null and this hook does nothing - Tilt is the solo machine it always was.
// ⚠ A refresh drops your row (onDisconnect), and with it your place in the run - unlike Mini Wili there is
//   no resume yet (pinned in TILT_PRIME §P2.W).
// ============================================================
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ref, update, remove, onValue, onDisconnect, runTransaction } from "firebase/database";
import { db } from "./firebase";
import { serverNow } from "./ieSocial";
import {
  RELAY_BASE, presentOrder, inRun, bankOf, isLive, hostOf, hostIntent, skipIntent, stripOf, joinable,
  enterServer, renameMe, startRun, joinRun, startBall, ballOver, skipTurn, endRun, assignTurn, leaveRun, unpackSnap,
} from "./tiltRelay";

export const FB_IO = {
  nowInt: () => Math.round(serverNow()),
  update: (paths) => update(ref(db), paths),
  cas: async (path, fn) => { const r = await runTransaction(ref(db, path), (cur) => fn(cur)); return !!r.committed; },
  onDisc: (path) => onDisconnect(ref(db, path)).remove(),
  cancelDisc: (path) => onDisconnect(ref(db, path)).cancel(),
  remove: (path) => remove(ref(db, path)),
};
const EMPTY = { run: null, turn: null, p: {} };

export function useRelay({ sid, uid, name, seats }) {
  const base = sid && uid ? RELAY_BASE(sid) : null;
  const [raw, setRaw] = useState(EMPTY);
  const [ready, setReady] = useState(false);
  const [passed, setPassed] = useState(-1);          // the turn.seq I just played out: no second ball while the pass is in flight
  const nameRef = useRef(name); nameRef.current = name;
  const nameSent = useRef(null);

  useEffect(() => {
    setRaw(EMPTY); setReady(false); setPassed(-1);
    if (!base) return undefined;
    const off = onValue(ref(db, base), (snap) => {
      const v = snap.val() || {};
      setRaw({ run: v.run || null, turn: v.turn || null, p: v.p || {} });
      setReady(true);
    }, () => {});
    return off;
  }, [base]);

  useEffect(() => {
    if (!base) return undefined;
    const me = { uid, n: nameRef.current, j: Math.round(serverNow()) };
    nameSent.current = me.n;
    enterServer(FB_IO, base, me).catch(() => {});
    return () => {
      FB_IO.cancelDisc(`${base}/p/${uid}`).catch(() => {});
      FB_IO.remove(`${base}/p/${uid}`).catch(() => {});
    };
  }, [base, uid]);
  useEffect(() => {
    if (!base || !name || nameSent.current === name) return;
    nameSent.current = name; renameMe(FB_IO, base, uid, name).catch(() => {});
  }, [base, uid, name]);

  const seatUids = useMemo(() => {
    const l = (seats || []).map((s) => s.uid);
    return l.length ? (l.includes(uid) ? l : [...l, uid]) : null;
  }, [seats, uid]);
  const d = useMemo(() => {
    const order = presentOrder(raw.p, seatUids);
    const run = raw.run;
    const me = order.find((p) => p.uid === uid) || null;
    const imIn = inRun(me, run);
    const turn = run && raw.turn && raw.turn.r === run.id ? raw.turn : null;
    const myTurn = !!(imIn && !run.over && turn && turn.uid === uid && turn.seq !== passed && bankOf(me, run) > 0);
    return {
      order, run, me, imIn, turn, myTurn,
      live: isLive(run, order),
      canJoin: !imIn && joinable(run, order, turn),
      myBank: bankOf(me, run),
      table: run ? unpackSnap(run.snap) : null,
      strip: stripOf(order, run, turn, uid),
      hostName: (order.find((p) => inRun(p, run)) || {}).n || null,
      turnName: turn ? ((order.find((p) => p.uid === turn.uid) || {}).n || null) : null,
    };
  }, [raw, seatUids, uid, passed]);
  const latest = useRef(d); latest.current = d;

  // the host keeps house: end a spent run, hand out a turn when none stands
  const did = useRef({ end: -1, asg: "" });
  useEffect(() => {
    if (!base || !ready || !d.run) return;
    const it = hostIntent({ order: d.order, run: d.run, turn: raw.turn, uid });
    if (!it) return;
    if (it.type === "end") {
      if (did.current.end === it.id) return;
      did.current.end = it.id;
      endRun(FB_IO, base, d.run).catch(() => { did.current.end = -1; });
    } else {
      const key = `${it.id}:${it.seq}`;
      if (did.current.asg === key) return;
      did.current.asg = key;
      assignTurn(FB_IO, base, d.run, d.order, raw.turn).catch(() => { did.current.asg = ""; });
    }
  }, [base, ready, d, raw.turn, uid]);

  // anyone else passes a stuck turn
  useEffect(() => {
    if (!base || !ready || !d.run) return undefined;
    const it = skipIntent({ order: d.order, run: d.run, turn: raw.turn, uid, now: serverNow() });
    if (!it) return undefined;
    const t = setTimeout(() => {
      const L = latest.current;
      if (L.run) skipTurn(FB_IO, base, it, L.order, L.run).catch(() => {});
    }, it.delay);
    return () => clearTimeout(t);
  }, [base, ready, d.order, d.run, raw.turn, uid]);

  const meObj = useMemo(() => ({ uid, n: name }), [uid, name]);
  const start = useCallback(() => (base ? startRun(FB_IO, base, meObj, latest.current.run).catch(() => {}) : Promise.resolve()), [base, meObj]);
  const join = useCallback(() => {
    const L = latest.current;
    if (!base || !L.run || !L.canJoin) return Promise.resolve(false);
    return joinRun(FB_IO, base, meObj, L.run, L.order).catch(() => false);
  }, [base, meObj]);
  const leave = useCallback(() => {
    const L = latest.current;
    return base ? leaveRun(FB_IO, base, meObj, L.run, L.turn, L.order).catch(() => {}) : Promise.resolve();
  }, [base, meObj]);
  // my turn: CLAIM the ball (a CAS on the turn). Resolves to the ball (what it starts from) only if the claim won -
  // otherwise null, and the lobby simply stays put (the turn already moved on).
  const shoot = useCallback(async () => {
    const L = latest.current;
    if (!base || !L.myTurn) return null;
    const ball = { before: L.table, turnSeq: L.turn.seq, runId: L.run.id, balls0: L.run.balls || 0 };
    const ok = await startBall(FB_IO, base, uid, L.turn).catch(() => false);
    if (!ok) return null;
    return ball;
  }, [base, uid]);
  // my ball drained: hand the table on (against the run AS IT WAS when I shot)
  const finish = useCallback((snap, ball) => {
    const L = latest.current;
    if (!base || !L.run || !ball) return Promise.resolve(null);
    setPassed(ball.turnSeq);
    return ballOver(FB_IO, base, meObj, L.run, L.turn, L.order,
      { snap, before: ball.before, balls0: ball.balls0, seq: ball.turnSeq }).catch(() => null);
  }, [base, meObj]);

  return { ...d, ready: !!base && ready, start, join, leave, shoot, finish };
}
